import { Col, Descriptions, Row, Statistic } from 'antd';
import type { SecurityEstimateInternalResponse } from '@wisecure/api-client';
import { success } from '@/theme';
import { formatEuro } from './labels';

interface InternalMarginBreakdownProps {
  internal: SecurityEstimateInternalResponse;
  /** Lay the three columns vertically (narrow cards). */
  compact?: boolean;
}

/** Internal margin breakdown of an estimate. Never shown to clients. */
export function InternalMarginBreakdown({ internal, compact }: InternalMarginBreakdownProps) {
  const span = compact ? 24 : 8;
  return (
    <div>
      <Row gutter={[24, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={compact ? 12 : 6}>
          <Statistic
            title="Marge Wisecure"
            value={formatEuro(internal.wisecureMargin)}
            valueStyle={{ color: success[500], fontWeight: 700 }}
          />
        </Col>
        <Col xs={12} md={compact ? 12 : 6}>
          <Statistic title="Taux de marge" value={`${internal.marginPct} %`} />
        </Col>
        <Col xs={12} md={compact ? 12 : 6}>
          <Statistic title="Encaissé (hors aléas)" value={formatEuro(internal.revenue)} />
        </Col>
        <Col xs={12} md={compact ? 12 : 6}>
          <Statistic title="Coût total" value={formatEuro(internal.totalCost)} />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={span}>
          <Descriptions title="Prix de vente" column={1} size="small" bordered>
            <Descriptions.Item label="Matériel">{formatEuro(internal.equipment)}</Descriptions.Item>
            <Descriptions.Item label="Fournitures">{formatEuro(internal.supplies)}</Descriptions.Item>
            <Descriptions.Item label="Main d'œuvre">{formatEuro(internal.labour)}</Descriptions.Item>
            <Descriptions.Item label="Mise en service">{formatEuro(internal.commissioning)}</Descriptions.Item>
            <Descriptions.Item label="Provision aléas">{formatEuro(internal.contingency)}</Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} lg={span}>
          <Descriptions title="Coûts" column={1} size="small" bordered>
            <Descriptions.Item label="Achat matériel">{formatEuro(internal.equipmentPurchase)}</Descriptions.Item>
            <Descriptions.Item label="Coût fournitures">{formatEuro(internal.suppliesCost)}</Descriptions.Item>
            <Descriptions.Item label="Rémunération installateur">{formatEuro(internal.installerPayout)}</Descriptions.Item>
            <Descriptions.Item label="Temps de pose">
              {internal.labourHours.toLocaleString('fr-FR')} h ({internal.billedHours.toLocaleString('fr-FR')} h facturées)
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} lg={span}>
          <Descriptions title="Marges" column={1} size="small" bordered>
            <Descriptions.Item label="Matériel">{formatEuro(internal.equipmentMargin)}</Descriptions.Item>
            <Descriptions.Item label="Fournitures">{formatEuro(internal.suppliesMargin)}</Descriptions.Item>
            <Descriptions.Item label="Pose + mise en service">{formatEuro(internal.installMargin)}</Descriptions.Item>
            <Descriptions.Item label="Total">{formatEuro(internal.wisecureMargin)}</Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>
    </div>
  );
}
